import dayjs from 'dayjs'

/**
 * 时间格式化，处理成统一的展示格式
 * @param date
 */
export function formatTime(date: string | number) {
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * 将数字限制在指定范围内
 * @param value 输入的数字
 * @param min 范围最小值（包含）
 * @param max 范围最大值（包含）
 * @returns 限制在[min, max]范围内的数字
 */
export function clampNumber(value: number | string, min: number, max: number) {
  if (!value) {
    return 0
  }
  else if (Number(value) < min) {
    value = '0'
  }
  else if (Number(value) > max) {
    value = max
  }
  return Number(value)
}

/**
 * 一键复制功能
 * @param copyText 需要复制的文本
 */
export function handleCopy(copyText: string | number) {
  uni.setClipboardData({
    data: copyText.toString(),
    success(res) {
      uni.showToast({
        title: '复制成功',
        icon: 'success',
        duration: 2000,
      })
    },
    fail(res) {
      uni.showToast({
        title: '复制失败',
        icon: 'none',
        duration: 2000,
      })
    },
  })
}

/**
 * 返回上一级
 */
export function navigateBack(backInfo?: { delta?: number }) {
  if (backInfo?.delta) {
    const routeList = getCurrentPages()
    if (routeList.length > backInfo.delta) {
      uni.navigateBack({
        delta: backInfo.delta,
        fail: () => {
          uni.switchTab({ url: '/pages/index/index' })
        },
      })
    }
    else {
      uni.switchTab({ url: '/pages/index/index' })
    }
  }
  else {
    uni.navigateBack()
  }
}

/**
 * 防抖函数
 * @param func 需要防抖的函数
 * @param wait 等待时间（毫秒）
 * @param immediate 是否立即执行（默认false，在延迟后执行）
 * @returns 防抖后的函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function (this: any, ...args: Parameters<T>) {
    const later = () => {
      timeout = null
      if (!immediate) {
        func.apply(this, args)
      }
    }

    const callNow = immediate && !timeout

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)

    if (callNow) {
      func.apply(this, args)
    }
  } as (...args: Parameters<T>) => void
}

/**
 * 节流函数
 * @param func 需要节流的函数
 * @param wait 等待时间（毫秒）
 * @param leading 是否在开始时执行（默认true）
 * @param trailing 是否在结束时执行（默认true）
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  leading: boolean = true,
  trailing: boolean = true,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let previous = 0

  return function (this: any, ...args: Parameters<T>) {
    const now = Date.now()

    if (!previous && !leading) {
      previous = now
    }

    const remaining = wait - (now - previous)

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      previous = now
      func.apply(this, args)
    }
    else if (!timeout && trailing) {
      timeout = setTimeout(() => {
        previous = leading ? Date.now() : 0
        timeout = null
        func.apply(this, args)
      }, remaining)
    }
  } as (...args: Parameters<T>) => void
}
